import { useErrorContext } from '@qpr/client-library';
import React, { ReactNode, useEffect, useState } from 'react';
import { Filter } from '../filter-state';
import createFilterViewModel, { FilterViewModelType } from '../views/filter-view-model';
import useUrlFiltersHelper from './useUrlFiltersHelper';

interface FilterProviderProps {
  children: ReactNode;
}

type FilterContextValue = {
  getFilters: () => Filter[];
  toggleFilterEnabled: (filterToToggle: Filter) => void;
  toggleFilterSelect: (filterToToggle: Filter) => void;
  addOrUpdateFilter: (filterToAdd: Filter) => Promise<boolean>;
  removeFilter: (filterToRemove: Filter) => void;
  getActiveFilters: () => Filter[];
  setFilters: (any: any) => any;
  setSearchFilter: (searchFilter: Filter | undefined) => void;
  getSearchFilter: () => Filter | undefined;
};

const FilterContext = React.createContext<FilterContextValue>({} as FilterContextValue);
export const useFilter = () => React.useContext(FilterContext);

const FilterProvider = ({ children }: FilterProviderProps) => {
  const [filters, setFilters] = useState<Filter[]>([]);
  const [searchFilter, setSearchFilter] = useState<Filter | undefined>();

  const urlFilters = useUrlFiltersHelper();
  const safelyParseJSON = (json: string) => {
    let parsed;

    try {
      parsed = JSON.parse(json);
    } catch (e) {
      return '';
    }

    return parsed;
  };

  const { setError } = useErrorContext();
  const viewModel: FilterViewModelType = createFilterViewModel();

  const getFilters = () => {
    return filters;
  };

  const toggleFilterEnabled = (filterToToggle: Filter) => {
    const updatedFilters = filters.map((filter: Filter) => {
      if (filter._id === filterToToggle._id) {
        const state = !filter.enabled;
        if (state) {
          urlFilters.addEnabledFilter(filter._id);
          urlFilters.removeSelectedFilter(filter._id);
        } else {
          urlFilters.removeEnabledFilter(filter._id);
          urlFilters.addSelectedFilter(filter._id);
        }
        urlFilters.replaceHistory();
        return { ...filter, enabled: state };
      }
      return filter;
    });

    setFilters(updatedFilters);
  };

  const toggleFilterSelect = (filterToToggle: Filter) => {
    const updatedFilters = filters.map((filter: Filter) => {
      if (filter._id === filterToToggle._id) {
        const state = !filter.selected;
        if (state) {
          urlFilters.addEnabledFilter(filter._id);
        } else {
          urlFilters.removeEnabledFilter(filter._id);
          urlFilters.removeSelectedFilter(filter._id);
        }

        urlFilters.replaceHistory();

        return { ...filter, selected: state, enabled: state };
      }
      return filter;
    });

    setFilters(updatedFilters);
  };

  const addOrUpdateFilter = async (filterToAdd: Filter) => {
    const savedFilterFromAPI = await viewModel.saveFilter({
      ...filterToAdd,
      rule: JSON.stringify(JSON.parse(filterToAdd.rule))
    });

    if (savedFilterFromAPI.error?.message) {
      setError(savedFilterFromAPI.error);
      return false;
    }

    const updatedFilter = {
      ...savedFilterFromAPI.data[0],
      rule: safelyParseJSON(savedFilterFromAPI.data[0].rule),
      enabled: true,
      selected: true
    };

    if (filters.find(item => item._id === updatedFilter._id) === undefined) {
      setFilters([...filters, updatedFilter]);
    } else {
      setFilters(
        filters.map((filter: Filter) => (filter._id === updatedFilter._id ? updatedFilter : filter))
      );
    }

    urlFilters.addEnabledFilter(savedFilterFromAPI.data[0]._id);
    urlFilters.replaceHistory();
    return true;
  };

  const removeFilter = async (filterToRemove: Filter) => {
    const deletedFilterFromAPI = await viewModel.deleteFilter(filterToRemove);

    if (deletedFilterFromAPI.data[0] && deletedFilterFromAPI.data[0]._id !== undefined) {
      setFilters(filters.filter((filter: Filter) => filter._id !== filterToRemove._id));
    }

    if (deletedFilterFromAPI.error?.message) {
      setError(deletedFilterFromAPI.error);
    }
  };

  const getActiveFilters = () =>
    filters.filter((filter: Filter) => filter.selected && filter.enabled);

  useEffect(() => {
    const initData = async () => {
      const gotData = await viewModel.getData();
      const parsedData = gotData.data.map((filter: Filter) => ({
        ...filter,
        rule: safelyParseJSON(filter.rule)
      }));
      setFilters(parsedData);
      if (gotData.error?.message) setError(gotData.error);
    };

    initData();
  }, []);

  const getSearchFilter = () => searchFilter;

  return (
    <FilterContext.Provider
      value={{
        getFilters,
        toggleFilterEnabled,
        addOrUpdateFilter,
        removeFilter,
        toggleFilterSelect,
        getActiveFilters,
        setFilters,
        getSearchFilter,
        setSearchFilter
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};

export default FilterProvider;
